'use client';

import clsx from 'clsx';
import { useContext, useMemo, type ForwardedRef, type ReactNode, type RefObject } from 'react';
import { useInteractOutside, useObjectRef } from 'react-aria';
import {
  DialogTrigger as RACDialogTrigger,
  Popover as RACPopover,
  type PopoverProps as RACPopoverProps,
  OverlayArrow as RACOverlayArrow,
  Heading as RACHeading,
  OverlayTriggerStateContext as RACOverlayTriggerStateContext,
  Dialog as RACDialog,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { FocusableContainer } from '../internal/focusable-container/focusable-container.js';
import type { AriaLabelingProps, CommonProps } from '../types.js';
import { textStyle } from '../typography/utils.js';

export interface PopoverRootProps {
  /** The popover trigger with its associated popover. Provide the trigger as the first child, and the popover as the second child. */
  children: ReactNode;
  /** Whether the popover is open (controlled). */
  isOpen?: boolean;
  /** Whether the popover is open by default (uncontrolled). */
  defaultOpen?: boolean;
  /** Handler that is called when the popover's open state changes. */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Encapsulates a popover trigger and its associated popover.
 * The trigger can be any Cimpress UI button, and the popover will be displayed when the trigger is activated.
 */
export function PopoverRoot(props: PopoverRootProps) {
  return <RACDialogTrigger {...props} />;
}

PopoverRoot.displayName = 'PopoverRoot';

interface DialogProps extends CommonProps, AriaLabelingProps {}

export interface PopoverRenderProps {
  /** Closes the popover when called. */
  close: () => void;
}

export interface PopoverProps extends DialogProps, Pick<RACPopoverProps, 'placement'> {
  /** The contents of the popover. A function may be provided to access a function to close the popover. */
  children: ReactNode | ((renderProps: PopoverRenderProps) => ReactNode);
  /** The title of the popover. */
  title?: string;
  /**
   * Whether the popover is open (controlled).
   * If using `PopoverRoot`, this prop has no effect - provide `isOpen` to `PopoverRoot` instead.
   */
  isOpen?: boolean;
  /**
   * Whether the popover is open by default (uncontrolled).
   * If using `PopoverRoot`, this prop has no effect - provide `defaultOpen` to `PopoverRoot` instead.
   */
  defaultOpen?: boolean;
  /**
   * Handler that is called when the popover's open state changes.
   * If using `PopoverRoot`, this prop has no effect - provide `onOpenChange` to `PopoverRoot` instead.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * The ref for the element which the popover positions itself with respect to.
   * When used within `PopoverRoot`, this is set automatically. Provide `triggerRef` only when `Popover` is used standalone.
   */
  triggerRef?: RefObject<Element | null>;
  /**
   * Allows the popover to be non-modal, meaning it will not block interactions outside the popover.
   * This is non-default behavior and can lead to accessibility and interaction issues. Use only if you have a specific, valid reason.
   */
  UNSTABLE_isNonModal?: boolean;
  /**
   * Limits the maximum height of the popover.
   */
  UNSTABLE_maxHeight?: number;
}

function Popover(
  {
    children,
    title,
    UNSAFE_className,
    UNSAFE_style,
    isOpen,
    defaultOpen,
    onOpenChange,
    placement,
    triggerRef,
    UNSTABLE_isNonModal,
    UNSTABLE_maxHeight,
    ...props
  }: PopoverProps,
  ref: ForwardedRef<HTMLElement>,
) {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!title && !ariaLabel && !ariaLabelledBy) {
      console.warn('Popover requires one of title / aria-label / aria-labelledby for accessibility');
    }
  }, [title, ariaLabel, ariaLabelledBy]);

  const popoverRef = useObjectRef(ref);

  return (
    <RACPopover
      ref={popoverRef}
      className={clsx('cim-popover', UNSAFE_className)}
      style={UNSAFE_style}
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      triggerRef={triggerRef}
      placement={placement}
      containerPadding={16}
      offset={5} // 1px border + 4px actual offset
      arrowBoundaryOffset={4} // offset by border radius so that the arrow is never positioned over the rounded corner
      isNonModal={UNSTABLE_isNonModal}
      maxHeight={UNSTABLE_maxHeight}
      data-cim-style-root
    >
      <PopoverInner
        dialogProps={props}
        title={title}
        children={children}
        popoverRef={popoverRef}
        isNonModal={UNSTABLE_isNonModal}
      />
    </RACPopover>
  );
}

interface PopoverInnerProps {
  dialogProps: Omit<DialogProps, 'UNSAFE_className' | 'UNSAFE_style'>;
  title?: string;
  children: ReactNode | ((renderProps: PopoverRenderProps) => ReactNode);
  popoverRef: RefObject<HTMLElement>;
  isNonModal?: boolean;
}

function PopoverInner({ title, children, popoverRef, isNonModal, dialogProps }: PopoverInnerProps) {
  const overlayTriggerState = useContext(RACOverlayTriggerStateContext);
  const renderProps = useMemo(() => ({ close: () => overlayTriggerState?.close() }), [overlayTriggerState]);

  // For non-modal popovers, React Aria does not handle outside interaction by default,
  // so we use a custom handler here. Modal popovers already handle this internally.
  useInteractOutside({
    ref: popoverRef,
    onInteractOutside: () => {
      // Delay closing to prevent race conditions: when the trigger is pressed, both the trigger and the interact outside handler fire.
      // Without this delay, the popover may immediately reopen after closing.
      requestAnimationFrame(() => {
        overlayTriggerState?.close();
      });
    },
    isDisabled: !isNonModal,
  });

  return (
    <>
      <RACOverlayArrow className="cim-popover-arrow">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10">
          <path d="M16 0L8.7809 9.0239C8.3805 9.5243 7.6195 9.5243 7.2191 9.0239L0 0" />
        </svg>
      </RACOverlayArrow>

      <RACDialog {...dialogProps} className="cim-popover-body">
        {title && (
          <RACHeading slot="title" className={clsx('cim-popover-title', textStyle({ variant: 'title-5' }))}>
            {title}
          </RACHeading>
        )}
        <FocusableContainer className="cim-popover-content" scrollDirection="vertical">
          {typeof children === 'function' ? children(renderProps) : children}
        </FocusableContainer>
      </RACDialog>
    </>
  );
}

/**
 * Displays an overlay element positioned relative to a trigger.
 *
 * See [popover usage guidelines](https://ui.cimpress.io/components/popover/).
 */
const _Popover = forwardRef(Popover, 'Popover');

export { _Popover as Popover };
